import { legendError, legendText } from './presentation';
import type { Legend, Purpose, WatermarkSettings } from './types';

// Formulario de la configuración de marca de agua (so=400). Puro: borrador editable,
// errores con los límites del backend, cuerpo exacto del PUT (reemplazo completo, que
// conserva leyendas de otras claves) y estilo para la vista previa en vivo.

export interface LegendDraft {
  text: string;
  enabled: boolean;
}

export interface WatermarkDraft {
  interna: LegendDraft;
  externa: LegendDraft;
  opacityPct: string;
  fontSizePt: string;
  rotationDeg: string;
  colorHex: string;
  tiled: boolean;
  onlyCurrent: boolean;
}

export type DraftField =
  | 'interna'
  | 'externa'
  | 'opacityPct'
  | 'fontSizePt'
  | 'rotationDeg'
  | 'colorHex';

export interface PreviewStyle {
  opacity: number;
  fontSizePt: number;
  rotationDeg: number;
  colorHex: string;
  tiled: boolean;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function intIn(raw: string, min: number, max: number): number | null {
  if (!/^-?\d{1,4}$/.test(raw.trim())) {
    return null;
  }
  const n = Number(raw.trim());
  return n >= min && n <= max ? n : null;
}

const legendOf = (legends: Legend[], key: Purpose): LegendDraft => ({
  text: legendText(legends, key),
  enabled: legends.find((l) => l.key === key)?.enabled ?? false,
});

export function draftFrom(s: WatermarkSettings): WatermarkDraft {
  return {
    interna: legendOf(s.legends, 'interna'),
    externa: legendOf(s.legends, 'externa'),
    opacityPct: String(Math.round(s.opacity * 100)),
    fontSizePt: String(s.fontSizePt),
    rotationDeg: String(s.rotationDeg),
    colorHex: s.colorHex,
    tiled: s.tiled,
    onlyCurrent: s.onlyCurrent,
  };
}

export function draftErrors(d: WatermarkDraft): Partial<Record<DraftField, string>> {
  const out: Partial<Record<DraftField, string>> = {};
  for (const key of ['interna', 'externa'] as const) {
    const err = d[key].enabled || d[key].text.trim() !== '' ? legendError(d[key].text) : null;
    if (err) {
      out[key] = err;
    }
  }
  if (intIn(d.opacityPct, 5, 100) === null) {
    out.opacityPct = 'Entre 5 y 100 %.';
  }
  if (intIn(d.fontSizePt, 6, 200) === null) {
    out.fontSizePt = 'Entre 6 y 200 pt.';
  }
  if (intIn(d.rotationDeg, -90, 90) === null) {
    out.rotationDeg = 'Entre -90 y 90 grados.';
  }
  if (!HEX.test(d.colorHex)) {
    out.colorHex = 'Color en formato #RRGGBB.';
  }
  return out;
}

/** Cuerpo del PUT; solo se llama con un borrador sin errores. */
export function draftBody(d: WatermarkDraft, base: WatermarkSettings): WatermarkSettings {
  const edited: Legend[] = (['interna', 'externa'] as const).map((key) => ({
    key,
    text: d[key].text.trim() || legendText(base.legends, key),
    enabled: d[key].enabled,
  }));
  const legends = base.legends.map((l) => edited.find((e) => e.key === l.key) ?? l);
  for (const e of edited) {
    if (!legends.some((l) => l.key === e.key) && e.text !== '') {
      legends.push(e);
    }
  }
  return {
    legends,
    opacity: (intIn(d.opacityPct, 5, 100) ?? Math.round(base.opacity * 100)) / 100,
    fontSizePt: intIn(d.fontSizePt, 6, 200) ?? base.fontSizePt,
    rotationDeg: intIn(d.rotationDeg, -90, 90) ?? base.rotationDeg,
    colorHex: HEX.test(d.colorHex) ? d.colorHex : base.colorHex,
    tiled: d.tiled,
    onlyCurrent: d.onlyCurrent,
  };
}

export function isDirty(d: WatermarkDraft, base: WatermarkSettings): boolean {
  return JSON.stringify(draftBody(d, base)) !== JSON.stringify(base);
}

/** Estilo que dibuja la vista previa: lo válido del borrador, si no lo guardado. */
export function previewStyle(d: WatermarkDraft, base: WatermarkSettings): PreviewStyle {
  const body = draftBody(d, base);
  return {
    opacity: body.opacity,
    fontSizePt: body.fontSizePt,
    rotationDeg: body.rotationDeg,
    colorHex: body.colorHex,
    tiled: body.tiled,
  };
}
